import { useState, useCallback } from 'react';
import { TagNoteDto } from 'api/note';
import { notesAPI } from 'services/swagger-api';

/** Default color for new custom tags */
const DEFAULT_TAG_COLOR = '#51A0AA';

/**
 * Hook to search and manage note tags with autocomplete
 */
export const useNoteTags = () => {
  const [suggestions, setSuggestions] = useState<TagNoteDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  /**
   * Search tags by text (for autocomplete)
   */
  const searchTags = useCallback(async (searchText: string) => {
    if (!searchText || searchText.length < 1) {
      setSuggestions([]);
      return;
    }

    setIsLoading(true);
    try {
      const response = await notesAPI.searchTagsAsync({
        searchTagsNotesCommand: { searchText }
      });
      setSuggestions(response.tags || []);
    } catch (error) {
      console.error('Error searching tags:', error);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Load all available tags (initial load)
   */
  const loadAllTags = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await notesAPI.searchTagsAsync({
        searchTagsNotesCommand: { searchText: '' }
      });
      setSuggestions(response.tags || []);
    } catch (error) {
      console.error('Error loading tags:', error);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Create a new custom tag
   */
  const createTag = useCallback(async (label: string): Promise<TagNoteDto | null> => {
    if (!label.trim()) return null;

    setIsCreating(true);
    try {
      const response = await notesAPI.createTagAsync({
        createTagNoteCommand: { label: label.trim(), color: DEFAULT_TAG_COLOR }
      });

      // Return the created tag with the response data
      // The API should return the created tag with its ID
      const newTag: TagNoteDto = {
        id: (response as any)?.id || `temp-${Date.now()}`,
        label: label.trim(),
        color: DEFAULT_TAG_COLOR,
        isGlobal: false
      };

      return newTag;
    } catch (error) {
      console.error('Error creating tag:', error);
      return null;
    } finally {
      setIsCreating(false);
    }
  }, []);

  /**
   * Clear suggestions
   */
  const clearSuggestions = useCallback(() => {
    setSuggestions([]);
  }, []);

  /**
   * Check if a tag can be permanently deleted from the database
   * Only non-global (custom) tags can be deleted
   */
  const canDeleteTagPermanently = useCallback((tag: TagNoteDto): boolean => {
    return !tag.isGlobal;
  }, []);

  /**
   * Permanently delete a tag from the database
   * This will remove the tag from all notes
   */
  const deleteTagPermanently = useCallback(
    async (tagId: string): Promise<boolean> => {
      if (!tagId) return false;

      setIsDeleting(true);
      try {
        await notesAPI.deleteTagAsync({ tagId });
        // Remove from suggestions list
        setSuggestions((prev) => prev.filter((t) => t.id !== tagId));
        return true;
      } catch (error) {
        console.error('Error deleting tag:', error);
        return false;
      } finally {
        setIsDeleting(false);
      }
    },
    []
  );

  return {
    suggestions,
    isLoading,
    isCreating,
    isDeleting,
    searchTags,
    loadAllTags,
    createTag,
    clearSuggestions,
    canDeleteTagPermanently,
    deleteTagPermanently
  };
};
